import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import "@/App.css";
import ScrollToTop from "./components/ScrollToTop";
import TabComebackMessage from "./components/TabComebackMessage";
import CartDrawer from "./components/CartDrawer";
import PublicLayout from "./components/PublicLayout";
import { AddressProvider } from "./context/AddressContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { SettingsProvider } from "./context/SettingsContext";

// Lightweight full-page loading spinner shown while chunks load
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTopColor: '#455a6b', borderRadius: '50%', animation: 'spin .6s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ── Public pages (lazy) ──────────────────────────────────────────────
const Home = React.lazy(() => import("./pages/Home"));
const ProductListing = React.lazy(() => import("./pages/ProductListing"));
const ProductDetail = React.lazy(() => import("./pages/ProductDetail"));
const ProductDetailV2 = React.lazy(() => import("./pages/ProductDetailV2"));
const Cart = React.lazy(() => import("./pages/Cart"));
const Login = React.lazy(() => import("./pages/Login"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const Wishlist = React.lazy(() => import("./pages/Wishlist"));
const Checkout = React.lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = React.lazy(() => import("./pages/CheckoutSuccess"));
const Orders = React.lazy(() => import("./pages/Orders"));
const OrderDetail = React.lazy(() => import("./pages/OrderDetail"));
const Receipt = React.lazy(() => import("./pages/Receipt"));
const Invoice = React.lazy(() => import("./pages/Invoice"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const About = React.lazy(() => import("./pages/About"));
const Products = React.lazy(() => import("./pages/Products"));
const Remake = React.lazy(() => import("./pages/Remake"));
const Profile = React.lazy(() => import("./pages/Profile"));
const Addresses = React.lazy(() => import("./pages/Addresses"));
const Diamonds = React.lazy(() => import("./pages/Diamonds"));
const DiamondDetails = React.lazy(() => import("./pages/DiamondDetails"));
const BookConsultation = React.lazy(() => import("./pages/BookConsultation"));
const AIBuilder = React.lazy(() => import("./pages/ai-builder/AIBuilder"));
const AIImagine = React.lazy(() => import("./pages/ai-builder/AIImagine"));
const AIDesignDetail = React.lazy(() => import("./pages/ai-builder/AIDesignDetail"));
const Portfolio = React.lazy(() => import("./pages/Portfolio"));
const PortfolioDetail = React.lazy(() => import("./pages/PortfolioDetail"));
const QuoteConfirmation = React.lazy(() => import("./pages/QuoteConfirmation"));
const Appraisal = React.lazy(() => import("./pages/Appraisal"));

// ── Admin pages (lazy) ──────────────────────────────────────────────
const AdminLayout = React.lazy(() => import("./pages/admin/AdminLayout"));
const AdminLogin = React.lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = React.lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetail = React.lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminCustomers = React.lazy(() => import("./pages/admin/AdminCustomers"));
const AdminDiamonds = React.lazy(() => import("./pages/admin/AdminDiamonds"));
const AdminSettings = React.lazy(() => import("./pages/admin/AdminSettings"));
const AdminProducts = React.lazy(() => import("./pages/admin/AdminProducts"));
const AdminPayments = React.lazy(() => import("./pages/admin/AdminPayments"));
const AdminAuditLogs = React.lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminShipping = React.lazy(() => import("./pages/admin/AdminShipping"));
const AdminShipmentDetail = React.lazy(() => import("./pages/admin/AdminShipmentDetail"));
const AdminPortfolio = React.lazy(() => import("./pages/admin/AdminPortfolio"));
const AdminPortfolioForm = React.lazy(() => import("./pages/admin/AdminPortfolioForm"));
const AdminTestimonials = React.lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminCalendly = React.lazy(() => import("./pages/admin/AdminCalendly"));
const AdminQuotes = React.lazy(() => import("./pages/admin/AdminQuotes"));
const AdminQuoteDetail = React.lazy(() => import("./pages/admin/AdminQuoteDetail"));
const AdminUsers = React.lazy(() => import("./pages/admin/AdminUsers"));
const AdminAppraisals = React.lazy(() => import("./pages/admin/AdminAppraisals"));

import { AdminProvider } from "./context/AdminContext";
import { Toaster } from "./components/ui/sonner";

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
        <CurrencyProvider>
          <AddressProvider>
            <AdminProvider>
              <ScrollToTop />
              <TabComebackMessage />
              <CartDrawer />
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Admin Routes - Standalone layout without public Header/Footer */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="orders/:id" element={<AdminOrderDetail />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="diamonds" element={<AdminDiamonds />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="shipping" element={<AdminShipping />} />
                  <Route path="shipping/:id" element={<AdminShipmentDetail />} />
                  <Route path="portfolio" element={<AdminPortfolio />} />
                  <Route path="portfolio/new" element={<AdminPortfolioForm />} />
                  <Route path="portfolio/:id" element={<AdminPortfolioForm />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route path="calendly" element={<AdminCalendly />} />
                  <Route path="quotes" element={<AdminQuotes />} />
                  <Route path="quotes/:quoteId" element={<AdminQuoteDetail />} />
                  <Route path="appraisals" element={<AdminAppraisals />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="audit-logs" element={<AdminAuditLogs />} />
                </Route>
                
                {/* Standalone Routes - No Header/Footer */}
                <Route path="/orders/:id/invoice" element={<Invoice />} />

                {/* Public Routes - With Header/Footer via PublicLayout */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/product-list/:category" element={<ProductListing />} />
                  <Route path="/product-list/:category/:filter" element={<ProductListing />} />
                  <Route path="/products/:category" element={<ProductListing />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/product-detail/:cmsId/:sku" element={<ProductDetail />} />
                  <Route path="/product-detail-v2/:cmsId/:sku" element={<ProductDetailV2 />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/checkout/success" element={<CheckoutSuccess />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:id" element={<OrderDetail />} />
                  <Route path="/orders/:id/receipt" element={<Receipt />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/contact" element={<ContactUs />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/remake" element={<Remake />} />
                  {/* <Route path="/categories" element={<Categories />} /> */}
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/addresses" element={<Addresses />} />
                  <Route path="/diamonds" element={<Diamonds />} />
                  <Route path="/diamond/:id" element={<DiamondDetails />} />
                  <Route path="/book-consultation" element={<BookConsultation />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/portfolio/:id" element={<PortfolioDetail />} />
                  <Route path="/remake/old" element={<AIBuilder />} />
                  <Route path="/remake/prompt" element={<AIImagine />} />
                  <Route path="/ai-design/:productId" element={<AIDesignDetail />} />
                  <Route path="/quote/confirmation/:quoteId" element={<QuoteConfirmation />} />
                  <Route path="/appraisal" element={<Appraisal />} />
                </Route>
              </Routes>
              </Suspense>
            </AdminProvider>
          </AddressProvider>
        </CurrencyProvider>
        </SettingsProvider>
      </BrowserRouter>
      <Toaster 
        position="bottom-center" 
        richColors 
        duration={2000}
        closeButton
        style={{
          bottom: '40px',
        }}
      />
    </div>
    </HelmetProvider>
  );
}

export default App;
